import React, { useMemo, useState, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { agentHue } from '@/frontend/lib/agentHue'
import { oklchToRgb, type Rgb } from '@/frontend/lib/oklch'

export interface AvatarProps {
  /** Image source URL */
  src?: string
  /** Alt text for image */
  alt?: string
  /** Name for initials fallback */
  name?: string
  /**
   * Stable seed (a digital worker id) that rotates the dither hue to the
   * agent's identity color (B.4). Omit for non-agent avatars (orgs, users),
   * which keep the fixed violet dither.
   */
  hueSeed?: string
  /** Avatar size (xxs = 20px mini da sidebar global: só o dither, sem iniciais) */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Shape variant */
  shape?: 'circle' | 'rounded'
  /** Status indicator */
  status?: 'online' | 'offline' | 'busy' | 'away'
  /** Additional class name */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
}

// Dither colors: deep purple base + lighter purple dots
const DITHER_BASE: Rgb = [76, 29, 149]    // #4C1D95 (violet-900)
const DITHER_DOT: Rgb  = [139, 92, 246]   // #8B5CF6 (violet-500)

// 4x4 Bayer dithering matrix (matching /session/new)
const BAYER = [
  0/16, 8/16, 2/16, 10/16,
  12/16, 4/16, 14/16, 6/16,
  3/16, 11/16, 1/16, 9/16,
  15/16, 7/16, 13/16, 5/16,
]

const PX = 2

// Deterministic hash from name
const hashName = (name: string): number => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

// Get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// Render Bayer-dithered pattern to canvas
const renderDither = (
  canvas: HTMLCanvasElement,
  phase: number,
  [base, dot]: readonly [Rgb, Rgb],
) => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const w = Math.ceil(canvas.clientWidth / PX)
  const h = Math.ceil(canvas.clientHeight / PX)
  if (w === 0 || h === 0) return
  canvas.width = w
  canvas.height = h

  const img = ctx.createImageData(w, h)
  const d = img.data

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const nx = x / w
      const ny = y / h
      // Diagonal linear gradient, angle varies per name
      const angle = 2.35 + phase * 0.3 // ~135deg base + per-name offset
      const brightness = (nx * Math.cos(angle) + ny * Math.sin(angle) + 1) / 2
      const threshold = BAYER[(y % 4) * 4 + (x % 4)]
      const c = brightness > threshold ? dot : base

      const i = (y * w + x) * 4
      d[i]     = c[0]
      d[i + 1] = c[1]
      d[i + 2] = c[2]
      d[i + 3] = 255
    }
  }

  ctx.putImageData(img, 0, 0)
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  hueSeed,
  size = 'md',
  shape = 'circle',
  status,
  className,
  'data-id': dataId,
}) => {
  const sizeClasses = {
    xxs: 'w-5 h-5',
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-[12px]',
    md: 'w-10 h-10 text-[14px]',
    lg: 'w-12 h-12 text-[16px]',
    xl: 'w-16 h-16 text-[20px]',
  }

  const statusSizeClasses = {
    xxs: 'w-2 h-2 border',
    xs: 'w-2 h-2 border',
    sm: 'w-2.5 h-2.5 border',
    md: 'w-3 h-3 border-2',
    lg: 'w-3.5 h-3.5 border-2',
    xl: 'w-4 h-4 border-2',
  }

  const statusColors = {
    online: 'bg-success',
    offline: 'bg-muted-foreground',
    busy: 'bg-destructive',
    away: 'bg-warning',
  }

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [src])

  const initials = name ? getInitials(name) : ''
  const showImage = src && !hasError

  const phase = useMemo(() => {
    const hash = name ? hashName(name) : 0
    return (hash % 100) / 10 // deterministic wave offset per name
  }, [name])

  // Same Bayer dither, hue rotated per agent (B.4): dark base + light dots keep
  // fixed L/C (only the OKLCH hue changes), so every agent stays in the same
  // color family while remaining distinguishable and stable across pages.
  const ditherPalette = useMemo((): readonly [Rgb, Rgb] => {
    if (!hueSeed) return [DITHER_BASE, DITHER_DOT]
    const hue = agentHue(hueSeed)
    return [oklchToRgb(0.35, 0.14, hue), oklchToRgb(0.72, 0.14, hue)]
  }, [hueSeed])

  useEffect(() => {
    if (showImage || !canvasRef.current) return
    renderDither(canvasRef.current, phase, ditherPalette)
  }, [showImage, phase, size, ditherPalette])

  return (
    <div
      className={cn('relative inline-flex shrink-0', className)}
      data-id={dataId}
    >
      <div
        aria-hidden={alt === '' ? true : undefined}
        className={cn(
          'relative overflow-hidden flex items-center justify-center font-extrabold text-white',
          shape === 'circle' ? 'rounded-full' : 'rounded-xl',
          sizeClasses[size],
        )}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt ?? name ?? 'Avatar'}
            className="w-full h-full object-cover"
            onError={() => setHasError(true)}
          />
        ) : (
          <>
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
              style={{ imageRendering: 'pixelated' }}
            />
            {/* No mini de 20px a identidade é só o dither com a hue do agente
                (B.4) — iniciais nesse tamanho virariam ruído ilegível. */}
            {size !== 'xxs' && <span className="relative">{initials}</span>}
          </>
        )}
      </div>
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-background',
            statusSizeClasses[size],
            statusColors[status]
          )}
        />
      )}
    </div>
  )
}
